import React from 'react';
import { logger } from '@/services/LoggingService';
import { InstitutionCard } from '../ui/InstitutionCard';
import type { AccountWithPositions } from '../ui/container-utils';

interface InstitutionsListProps {
  institutionGroups: any[];
  storeConnections: any[];
  connectionsLoading: boolean;
  multiStore: any;
  wealthicaAccountsLength: number;
  accountsWithPositions: AccountWithPositions[];
  loadingPositionsFor: any;
  isLoadingPositions: boolean;
  expandedAccounts: string[];
  setExpandedAccountsList: (expanded: string[]) => void;
  loadPositionsForAccount: (accountId: string) => void | Promise<void>;
  onPositionClick: (pos: any) => void;
  defaultLastSyncAt: any;
  disconnectAccount: (
    vars: { institutionId: string },
    options: { onSuccess: () => void; onError: (error: any) => void }
  ) => void;
}

export function InstitutionsList({
  institutionGroups,
  storeConnections,
  connectionsLoading,
  multiStore,
  wealthicaAccountsLength,
  accountsWithPositions,
  loadingPositionsFor,
  isLoadingPositions,
  expandedAccounts,
  setExpandedAccountsList,
  loadPositionsForAccount,
  onPositionClick,
  defaultLastSyncAt,
  disconnectAccount,
  
}: InstitutionsListProps) {
  return (
    <div className="space-y-4">
      {institutionGroups.map((institutionGroup) => {
        // Find matching connection for refresh functionality
        const storeConn = storeConnections.find((conn: any) => conn.institutionId === institutionGroup.id);
        // Retrieve per-institution last accounts fetch time from multi-store (authoritative for UI)
        const connState = typeof multiStore.getConnection === 'function'
          ? multiStore.getConnection(institutionGroup.id)
          : undefined;

        if (!storeConn) {
          if (!connectionsLoading && (storeConnections.length || 0) > 0) {
            logger.warn('Wealthica2GoldContainer', 'No connection found for institution (rendering with fallback)', {
              institutionId: institutionGroup.id,
              institutionName: institutionGroup.name,
            });
          } else {
            logger.debug(
              'Wealthica2GoldContainer',
              'Awaiting connections to match institution group (rendering with fallback)',
              {
                institutionId: institutionGroup.id,
                institutionName: institutionGroup.name,
                connectionsLoading,
                connectionCount: storeConnections.length || 0,
              }
            );
          }
        }

        logger.debug('Wealthica2GoldContainer', 'Rendering InstitutionCard with calculated balances', {
          institutionId: institutionGroup.id,
          institutionName: institutionGroup.name,
          accountCount: institutionGroup.accounts.length,
          totalCalculatedBalance: institutionGroup.accounts.reduce(
            (sum: number, acc: any) => sum + (acc.calculatedBalance || 0),
            0
          ),
          storeMode: 'multi-store',
        });

        // During the very first load, show skeletons only while a real fetch is in-flight.
        // If there are truly no positions (cash-only) and no fetch is running, do not show
        // indefinite "Loading positions..." placeholders.
        const firstPositionsPending = (wealthicaAccountsLength || 0) > 0 && (accountsWithPositions?.length || 0) === 0;
        const showFirstPositionsPending = firstPositionsPending && (isLoadingPositions || !!connState?.isLoadingPositions);

        return (
          <InstitutionCard
            key={institutionGroup.id}
            institution={institutionGroup}
            expandedAccounts={expandedAccounts}
            accountsWithPositions={accountsWithPositions || []}
            loadingPositionsFor={loadingPositionsFor}
            isInstitutionPositionsLoading={
              showFirstPositionsPending || isLoadingPositions || !!connState?.isLoadingPositions
            }
            isInstitutionBusy={
              !!connState?.isLoadingAccounts ||
              !!connState?.isPersisting ||
              !!connState?.isLoadingPositions ||
              isLoadingPositions ||
              connState?.processingState === 'processing'
            }
            onExpandedChange={setExpandedAccountsList}
            onLoadPositions={loadPositionsForAccount}
            onPositionClick={onPositionClick}
            onRefresh={async (institutionId: string) => {
              try {
                logger.info('Wealthica2GoldContainer', 'Institution-specific refresh initiated', {
                  institutionId,
                  institutionName: institutionGroup.name,
                });

                // Find the specific connection for this institution
                const connectionToRefresh = storeConnections.find((c: any) => c.institutionId === institutionId);
                // Use fresh connection state at the time of click
                const latestConn =
                  typeof multiStore.getConnection === 'function' ? multiStore.getConnection(institutionId) : connState;
                const isBusy =
                  !!latestConn?.isLoadingAccounts ||
                  !!latestConn?.isPersisting ||
                  !!latestConn?.isLoadingPositions ||
                  latestConn?.processingState === 'processing';
                if (isBusy) {
                  logger.debug('Wealthica2GoldContainer', 'Refresh skipped - institution currently busy', { institutionId });
                  return;
                }

                if (connectionToRefresh) {
                  logger.info('Wealthica2GoldContainer', 'Refreshing specific connection', {
                    institutionId,
                    providerName: connectionToRefresh.providerName,
                    storeMode: 'multi-store',
                  });

                  // Refresh accounts for this specific institution and await positions + persistence inside the store
                  try {
                    await multiStore.refreshAccountsForInstitution(institutionId);
                    logger.info(
                      'Wealthica2GoldContainer',
                      'Institution refresh completed (accounts + positions + persistence handled by store)',
                      {
                        institutionId,
                        storeMode: 'multi-store',
                      }
                    );
                  } finally {
                    // no-op: button-level spinner handles loading UI
                  }
                } else {
                  // Fallback: DB connection snapshot may be empty; try store-based refresh
                  logger.warn(
                    'Wealthica2GoldContainer',
                    'Connection not found for refresh, attempting store-based refresh',
                    { institutionId }
                  );
                  if (typeof multiStore.getConnection === 'function' && multiStore.getConnection(institutionId)) {
                    try {
                      await multiStore.refreshAccountsForInstitution(institutionId);
                      logger.info('Wealthica2GoldContainer', 'Store-based refresh completed', {
                        institutionId,
                        storeMode: 'multi-store',
                      });
                    } finally {
                      // no-op
                    }
                  } else {
                    logger.error(
                      'Wealthica2GoldContainer',
                      'Refresh skipped - no connection available in DB or store',
                      { institutionId }
                    );
                    throw new Error(`Connection not available for institution ${institutionId}`);
                  }
                }
              } catch (error) {
                logger.error('Wealthica2GoldContainer', 'Institution refresh failed', { institutionId, error });
                throw error;
              }
            }}
            onDisconnect={async (institutionId: string) => {
              try {
                logger.info('Wealthica2GoldContainer', 'Institution-specific disconnect initiated', {
                  institutionId,
                  storeMode: 'multi-store',
                });

                // Use the disconnect mutation
                await new Promise<void>((resolve, reject) => {
                  disconnectAccount(
                    { institutionId },
                    {
                      onSuccess: () => {
                        logger.info('Wealthica2GoldContainer', 'Disconnect successful', { institutionId });
                        resolve();
                      },
                      onError: (error) => {
                        logger.error('Wealthica2GoldContainer', 'Disconnect failed', { institutionId, error });
                        reject(error);
                      },
                    }
                  );
                });

                // Wait a moment for cleanup
                setTimeout(() => {
                  // The disconnect hook should handle store cleanup automatically
                  logger.info('Wealthica2GoldContainer', 'Disconnect cleanup completed', { institutionId });
                }, 1000);
              } catch (error) {
                logger.error('Wealthica2GoldContainer', 'Disconnect failed', { institutionId, error });
                // Re-throw to let InstitutionCard handle the error display
                throw error;
              }
            }}
            lastSyncAt={connState?.lastAccountsFetch || defaultLastSyncAt || null}
          />
        );
      })}
    </div>
  );
}
